import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatINR, formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Check } from "lucide-react";

const emptyAddress = {
  fullName: "", email: "", phone: "",
  line1: "", line2: "", city: "", state: "", pinCode: "", country: "India",
};

export default function Checkout() {
  const { items, subtotal, coupon, clearCart } = useCart();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [address, setAddress] = useState(() => {
    try { return JSON.parse(localStorage.getItem("candle_co_address")) || emptyAddress; }
    catch { return emptyAddress; }
  });
  const [shippingMethod, setShippingMethod] = useState("standard");
  const [payment, setPayment] = useState("upi");
  const [upiId, setUpiId] = useState("");
  const [card, setCard] = useState({ name: "", number: "", expiry: "", cvv: "" });
  const [tax, setTax] = useState(0);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (items.length === 0) navigate("/shop");
  }, [items, navigate]);

  const discount = coupon?.discount || 0;
  const shipping = useMemo(() => {
    if (shippingMethod === "express") return 150;
    return subtotal >= 999 ? 0 : 79;
  }, [shippingMethod, subtotal]);

  const total = Math.max(0, subtotal - discount) + tax + shipping;

  const requiredFilled = address.fullName && address.email && address.phone && address.line1 && address.city && address.state && address.pinCode;

  const computeTax = async () => {
    if (!address.pinCode) return;
    try {
      const { data } = await api.post("/tax/calculate", { pinCode: address.pinCode, subtotal: Math.max(0, subtotal - discount) });
      setTax(data.tax);
    } catch { setTax(0); }
  };

  useEffect(() => { if (step >= 2) computeTax(); /* eslint-disable-next-line */ }, [step, address.pinCode, subtotal, discount]);

  const submitInfo = (e) => {
    e.preventDefault();
    if (!requiredFilled) { toast.error("Please complete all required fields"); return; }
    localStorage.setItem("candle_co_address", JSON.stringify(address));
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const placeOrder = async () => {
    if (processing) return;
    setProcessing(true);
    try {
      const payload = {
        items: items.map((it) => ({
          productId: it.productId, name: it.name, image: it.image,
          variant: it.variant, quantity: it.quantity, unitPrice: it.unitPrice,
        })),
        address, couponCode: coupon?.code || null,
        paymentMethod: payment, shippingMethod,
      };
      // Simulate a brief payment processing delay
      await new Promise((r) => setTimeout(r, 1200));
      const { data } = await api.post("/orders", payload);
      clearCart();
      navigate(`/order/${data.orderNumber}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Payment failed. Please try again.");
      setProcessing(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {/* Steps */}
      <div className="flex items-center justify-center gap-3 sm:gap-6 mb-10">
        {["Information", "Review", "Payment"].map((label, i) => {
          const n = i + 1;
          return (
            <div key={label} className="flex items-center gap-2" data-testid={`checkout-step-indicator-${n}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition
                ${step >= n ? "bg-candle-terracotta text-white" : "bg-[hsl(var(--muted))] text-muted-foreground"}`}>
                {step > n ? <Check className="w-4 h-4" /> : n}
              </div>
              <div className={`hidden sm:block text-sm ${step === n ? "font-medium" : "text-muted-foreground"}`}>{label}</div>
              {n < 3 && <div className={`w-8 sm:w-16 h-px ${step > n ? "bg-candle-terracotta" : "bg-[hsl(var(--border))]"}`} />}
            </div>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {step === 1 && (
            <form onSubmit={submitInfo} className="p-6 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] space-y-4" data-testid="checkout-info-form">
              <h2 className="font-serif text-2xl">Your Information</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full Name*" v={address.fullName} onC={(v) => setAddress((s) => ({ ...s, fullName: v }))} tid="info-fullName" />
                <Field label="Email*" type="email" v={address.email} onC={(v) => setAddress((s) => ({ ...s, email: v }))} tid="info-email" />
                <Field label="Phone*" v={address.phone} onC={(v) => setAddress((s) => ({ ...s, phone: v }))} tid="info-phone" />
                <Field label="House/Flat No.*" v={address.line1} onC={(v) => setAddress((s) => ({ ...s, line1: v }))} tid="info-line1" />
                <Field label="Street/Area" v={address.line2} onC={(v) => setAddress((s) => ({ ...s, line2: v }))} tid="info-line2" />
                <Field label="City*" v={address.city} onC={(v) => setAddress((s) => ({ ...s, city: v }))} tid="info-city" />
                <Field label="State*" v={address.state} onC={(v) => setAddress((s) => ({ ...s, state: v }))} tid="info-state" />
                <Field label="PIN Code*" v={address.pinCode} onC={(v) => setAddress((s) => ({ ...s, pinCode: v }))} tid="info-pin" />
                <Field label="Country" v={address.country} onC={(v) => setAddress((s) => ({ ...s, country: v }))} tid="info-country" />
              </div>
              <Button data-testid="checkout-submit-info" type="submit" size="lg" className="w-full rounded-full">
                Continue to Review
              </Button>
            </form>
          )}

          {step === 2 && (
            <div className="p-6 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] space-y-6" data-testid="checkout-review">
              <h2 className="font-serif text-2xl">Review Your Order</h2>
              <div className="space-y-3">
                {items.map((it, i) => (
                  <div key={i} className="flex gap-3 items-center">
                    <img src={it.image} alt={it.name} className="w-16 h-16 rounded-lg object-cover" />
                    <div className="flex-1">
                      <div className="font-medium">{it.name}</div>
                      <div className="text-xs text-muted-foreground">Qty {it.quantity} · {formatINR(it.unitPrice)} each</div>
                    </div>
                    <div className="font-medium">{formatINR(it.unitPrice * it.quantity)}</div>
                  </div>
                ))}
              </div>

              <div className="border-t border-[hsl(var(--border))] pt-4">
                <div className="text-sm font-medium mb-2">Shipping to</div>
                <div className="text-sm text-muted-foreground">
                  {address.fullName}, {address.line1}{address.line2 ? `, ${address.line2}` : ""}, {address.city}, {address.state} {address.pinCode}
                  <br />{address.email} · {address.phone}
                </div>
              </div>

              <div className="border-t border-[hsl(var(--border))] pt-4">
                <div className="text-sm font-medium mb-2">Shipping method</div>
                <RadioGroup value={shippingMethod} onValueChange={setShippingMethod} className="space-y-2">
                  <ShipOpt v="standard" label="Standard · 3–5 days" price={subtotal >= 999 ? "Free" : formatINR(79)} />
                  <ShipOpt v="express" label="Express Glow · 1–2 days" price={formatINR(150)} />
                </RadioGroup>
              </div>

              <div className="flex gap-3">
                <Button data-testid="review-back" variant="outline" onClick={() => setStep(1)} className="rounded-full flex-1">Back</Button>
                <Button data-testid="review-continue-payment" onClick={() => setStep(3)} className="rounded-full flex-1">Continue to Payment</Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="p-6 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] space-y-6" data-testid="checkout-payment">
              <h2 className="font-serif text-2xl">Choose Your Payment Method</h2>
              <RadioGroup value={payment} onValueChange={setPayment} className="grid sm:grid-cols-2 gap-3">
                <PayOpt v="upi" label="UPI · GPay / PhonePe" />
                <PayOpt v="card" label="Credit / Debit Card" />
                <PayOpt v="netbanking" label="Net Banking" />
                <PayOpt v="cod" label="Cash on Delivery" />
              </RadioGroup>

              {payment === "upi" && (
                <div>
                  <label className="text-sm font-medium">UPI ID</label>
                  <input data-testid="pay-upi-id" value={upiId} onChange={(e) => setUpiId(e.target.value)}
                    placeholder="yourname@upi" className="mt-1 w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm" />
                </div>
              )}
              {payment === "card" && (
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Cardholder Name" v={card.name} onC={(v) => setCard((s) => ({ ...s, name: v }))} tid="pay-card-name" />
                  <Field label="Card Number" v={card.number} onC={(v) => setCard((s) => ({ ...s, number: v }))} tid="pay-card-number" />
                  <Field label="Expiry (MM/YY)" v={card.expiry} onC={(v) => setCard((s) => ({ ...s, expiry: v }))} tid="pay-card-expiry" />
                  <Field label="CVV" v={card.cvv} onC={(v) => setCard((s) => ({ ...s, cvv: v }))} tid="pay-card-cvv" />
                  <div className="sm:col-span-2 text-xs text-muted-foreground">Card details are not stored. In production, integrate a secure gateway (Razorpay/Stripe).</div>
                </div>
              )}

              <div className="flex gap-3">
                <Button data-testid="payment-back" variant="outline" onClick={() => setStep(2)} className="rounded-full flex-1">Back</Button>
                <Button data-testid="checkout-submit-payment" onClick={placeOrder} disabled={processing}
                  className="rounded-full flex-1 bg-candle-terracotta hover:bg-candle-terracotta/90">
                  {processing ? "Processing…" : `Pay ${formatINR(total)}`}
                </Button>
              </div>
              <div className="text-xs text-muted-foreground text-center">🔒 Demo payment · No real charge will occur.</div>
            </div>
          )}
        </div>

        <aside className="p-6 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] h-fit lg:sticky lg:top-24 space-y-3">
          <div className="font-serif text-2xl">Summary</div>
          <div className="space-y-2 text-sm py-3 border-t border-b border-[hsl(var(--border))]">
            <Row label={`Items (${items.reduce((s, i) => s + i.quantity, 0)})`} value={formatINR(subtotal)} />
            {discount > 0 && <Row label={`Discount ${coupon?.code}`} value={`−${formatINR(discount)}`} accent />}
            <Row label="Tax (GST)" value={formatINR(tax)} />
            <Row label="Shipping" value={shipping === 0 ? "Free" : formatINR(shipping)} />
          </div>
          <div className="flex justify-between items-baseline">
            <div className="font-medium">Total</div>
            <div className="font-serif text-2xl">{formatINR(total)}</div>
          </div>
          <div className="text-xs text-muted-foreground pt-2">
            Taxes calculated per configured business rules from your PIN code.
          </div>
        </aside>
      </div>
    </div>
  );
}

const Field = ({ label, v, onC, type = "text", tid }) => (
  <label className="block">
    <span className="text-xs font-medium text-muted-foreground uppercase tracking-widest">{label}</span>
    <input type={type} value={v} onChange={(e) => onC(e.target.value)} data-testid={tid}
      className="mt-1 w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm focus:outline-none focus:ring-2 focus:ring-candle-terracotta/40" />
  </label>
);

const Row = ({ label, value, accent }) => (
  <div className="flex justify-between">
    <span className="text-muted-foreground">{label}</span>
    <span className={accent ? "text-candle-terracotta font-medium" : "font-medium"}>{value}</span>
  </div>
);

const ShipOpt = ({ v, label, price }) => (
  <div className="flex items-center justify-between p-3 rounded-lg border border-[hsl(var(--border))]">
    <div className="flex items-center gap-3">
      <RadioGroupItem value={v} id={`ship-${v}`} data-testid={`ship-${v}`} />
      <Label htmlFor={`ship-${v}`}>{label}</Label>
    </div>
    <span className="text-sm font-medium">{price}</span>
  </div>
);
const PayOpt = ({ v, label }) => (
  <div className="flex items-center gap-3 p-3 rounded-lg border border-[hsl(var(--border))] cursor-pointer">
    <RadioGroupItem value={v} id={`pay-${v}`} data-testid={`pay-${v}`} />
    <Label htmlFor={`pay-${v}`} className="cursor-pointer">{label}</Label>
  </div>
);
